import { Filter, Calendar } from 'assets/icons';
import AdminSearchInput from 'components/AdminSearch/AdminSearchInput';
import ThreeStateCheckbox from 'components/threeStateCheckbox/threeStateCheckbox';
import UseContext from 'hooks/useContext';
import useImportedTimeSheet from 'hooks/useImportedTimeSheet';
import MediaQuery from 'react-responsive';
import { useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';
import { Outlet, useLocation, Navigate } from 'react-router-dom';
import ApiDatabase from 'server';
import PageForm from '../../components/pageForm/PageForm';
import SubNavbar from '../../components/SubNavbar/SubNavbar';
import TimeSheetSubNav from '../../components/subnav/timeSheet.subNav';
import Button from '../../components/button';
import BottomSheet from '../../components/bottomSheet/BottomSheet';
import { formatDateToYMD } from '../../helper/helper';

const SEARCH_TYPE_OPTIONS = [
  { value: '', label: 'Tous' },
  { value: 'id_time_sheet', label: 'ID Relevé Heure' },
  { value: 'id_mission', label: 'ID Mission' },
  { value: 'id_contract_bestt', label: 'ID BeSTT Contrat' },
  { value: 'id_bestt_company', label: 'ID BeSTT Entreprise' },
  { value: 'id_bestt_temp_worker', label: 'ID BeSTT Intérimaire' },
  { value: 'name_company', label: 'Nom Entreprise' },
  { value: 'temp_worker_name', label: 'Nom Intérimaire' },
  { value: 'temp_worker_email', label: 'Email Intérimaire' },
  { value: 'temp_worker_phone', label: 'Téléphone Intérimaire' },
];

const SearchTypeSelect = ({ value, onChange, className = '' }) => (
  <select
    className={`w-full h-10 pl-2 pr-3 ft-b placeholder-gray-600 border rounded-md text-sm focus:border-yellow-500 focus:outline-none ${className}`}
    onChange={(e) => onChange(e.target.value)}
    value={value}
  >
    {SEARCH_TYPE_OPTIONS.map((opt) => (
      <option key={opt.value} value={opt.value}>{opt.label}</option>
    ))}
  </select>
);

const TimeSheet = () => {
  const location = useLocation();
  const { companyData, refreshTime } = UseContext();

  const [arrayTimeSheets, setArrayTimeSheets] = useState([]);
  const [isWaiting, setIsWaiting] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [limitElement] = useState(20);
  const [countPerPage, setCountPerPage] = useState(limitElement);
  const [countTotal, setCountTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [timeSheetSearch, setTimeSheetSearch] = useState('');
  const [debouncedTimeSheetSearch, setDebouncedTimeSheetSearch] = useState('');
  const [timeSheetSearchType, setTimeSheetSearchType] = useState('');
  const [dateRange, setDateRange] = useState([null, null]);
  const [status, setStatus] = useState('1');
  const [isTimeSheetImported, setIsTimeSheetImported] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const [tempSearch, setTempSearch] = useState('');
  const [tempSearchType, setTempSearchType] = useState('');
  const [tempDateRange, setTempDateRange] = useState([null, null]);
  const [tempIsImported, setTempIsImported] = useState(false);

  const [startDate, endDate] = dateRange;
  const [tempStartDate, tempEndDate] = tempDateRange;

  const { timeSheetVerified } = useImportedTimeSheet();
  const token = localStorage.getItem('xsrfToken');

  const openFilterSheet = () => {
    setTempSearch(timeSheetSearch);
    setTempSearchType(timeSheetSearchType);
    setTempDateRange(dateRange);
    setTempIsImported(isTimeSheetImported);
    setIsFilterOpen(true);
  };

  const applyFilters = () => {
    setTimeSheetSearch(tempSearch);
    setTimeSheetSearchType(tempSearchType);
    setDateRange(tempDateRange);
    setIsTimeSheetImported(tempIsImported);
    setIsFilterOpen(false);
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [timeSheetSearch, timeSheetSearchType, startDate, endDate, status, isTimeSheetImported]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedTimeSheetSearch(timeSheetSearch);
    }, 300);

    return () => clearTimeout(timeout);
  }, [timeSheetSearch]);

  useEffect(() => {
    setIsWaiting(true);
    ApiDatabase.getTimeSheets(
      {
        token,
        status,
        page: currentPage,
        limit: limitElement,
        searchOptions: {
          search: debouncedTimeSheetSearch,
          type: timeSheetSearchType,
          startDate: startDate ? formatDateToYMD(startDate) : null,
          endDate: endDate ? formatDateToYMD(endDate) : null,
          isTimeSheetImported,
        },
      },
      (data) => {
        setArrayTimeSheets(data.timeSheets || []);
        setCountPerPage(data.countPerPage || limitElement);
        setCountTotal(data.count || 0);
        setTotalPages(data.countPages || 1);
        setIsWaiting(false);
      },
      (err) => { if (err.name !== 'CanceledError') console.log(err); }
    );
  }, [
    companyData,
    refreshTime,
    startDate,
    endDate,
    status,
    isTimeSheetImported,
    currentPage,
    limitElement,
    debouncedTimeSheetSearch,
    timeSheetSearchType,
  ]);

  const timeSheetsToDisplay = arrayTimeSheets;

  if (location.pathname === '/timeSheet') {
    const lastTab = sessionStorage.getItem('lastTimeSheetTab') || 'toBeCompleted';
    return <Navigate to={`/timeSheet/${lastTab}`} replace />;
  }

  return (
    <PageForm>
      <>
        {!location.pathname.startsWith('/timeSheet/ficheHour/') && (
          <SubNavbar>
            <div className="flex justify-between items-center w-full gap-3">
              <MediaQuery minWidth={768}>
                <div className="flex-1 overflow-x-auto overflow-y-hidden scrollbar-hide">
                  <div className="flex gap-3 items-center min-w-max">
                    <div className="text-sm relative z-30">
                      <div className="relative">
                        <div className="absolute left-0 top-0 h-10 flex items-center pl-2.5 pointer-events-none z-[1]">
                          <Calendar wh={16} color="#6B7280" />
                        </div>
                        <DatePicker
                          selectsRange
                          startDate={startDate}
                          endDate={endDate}
                          onChange={setDateRange}
                          isClearable
                          placeholderText="Sélectionner une période"
                          className="w-full min-w-[180px] h-10 pl-9 pr-8 ft-b border rounded-md text-left text-sm focus:border-yellow-500 focus:outline-none"
                          locale="fr-FR"
                          dateFormat="dd/MM/yyyy"
                          popperPlacement="bottom-start"
                          popperProps={{ strategy: 'fixed' }}
                        />
                      </div>
                    </div>
                    <div className="flex gap-2 items-center">
                      <div className="flex-1 max-w-[280px] text-sm searchbar-time-sheet">
                        <AdminSearchInput
                          placeholder="Rechercher"
                          showSearchRules
                          value={timeSheetSearch}
                          onChange={(e) => setTimeSheetSearch(e.target.value)}
                        />
                      </div>
                      <div className="max-w-[120px]">
                        <SearchTypeSelect
                          value={timeSheetSearchType}
                          onChange={setTimeSheetSearchType}
                          className="options-time-sheet"
                        />
                      </div>
                      {timeSheetVerified && (
                        <div className="border rounded py-2 px-4 text-sm imported-time-sheet">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <ThreeStateCheckbox checked={isTimeSheetImported} onChange={setIsTimeSheetImported} />
                            <div>Importé</div>
                          </label>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </MediaQuery>

              <MediaQuery maxWidth={767}>
                <div className="flex items-center gap-3 min-w-max">
                  <Button color="SECONDARY" size="SM" onClick={openFilterSheet}>
                    <Filter wh={16} color="#374151" />
                    <span>Filtrer</span>
                  </Button>
                  <div className="h-8 w-px bg-gray-200" />
                  <TimeSheetSubNav />
                </div>
              </MediaQuery>

              <MediaQuery minWidth={768}>
                <div className="flex-shrink-0 flex items-center gap-3">
                  <div className="h-8 w-px bg-gray-200" />
                  <TimeSheetSubNav />
                </div>
              </MediaQuery>
            </div>
          </SubNavbar>
        )}

        <div className="flex flex-col w-full h-full overflow-hidden">
          <div className="flex-1 overflow-y-auto">
            <Outlet
              context={{
                timeSheetsToDisplay,
                currentPage,
                setCurrentPage,
                totalPages,
                countPerPage,
                countTotal,
                isWaiting,
                setStatus,
              }}
            />
          </div>
        </div>

        <BottomSheet isOpen={isFilterOpen} onClose={() => setIsFilterOpen(false)} title="Filtrer">
          <div className="flex flex-col gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Période</label>
              <div className="relative">
                <div className="absolute left-0 top-0 h-10 flex items-center pl-2.5 pointer-events-none z-[1]">
                  <Calendar wh={16} color="#6B7280" />
                </div>
                <DatePicker
                  selectsRange
                  startDate={tempStartDate}
                  endDate={tempEndDate}
                  onChange={setTempDateRange}
                  isClearable
                  placeholderText="Sélectionner une période"
                  className="w-full h-10 pl-9 pr-8 ft-b border rounded-lg text-left text-sm focus:border-yellow-500 focus:outline-none"
                  locale="fr-FR"
                  dateFormat="dd/MM/yyyy"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Rechercher</label>
              <AdminSearchInput
                placeholder="Rechercher..."
                showSearchRules
                value={tempSearch}
                onChange={(e) => setTempSearch(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Rechercher par</label>
              <SearchTypeSelect
                value={tempSearchType}
                onChange={setTempSearchType}
                className="rounded-lg"
              />
            </div>
            {timeSheetVerified && (
              <div className="border rounded-lg py-3 px-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <ThreeStateCheckbox checked={tempIsImported} onChange={setTempIsImported} />
                  <span className="text-sm">Relevés importés</span>
                </label>
              </div>
            )}
            <div className="mt-2">
              <Button color="PRIMARY" size="FULL" onClick={applyFilters}>Appliquer</Button>
            </div>
          </div>
        </BottomSheet>
      </>
    </PageForm>
  );
};

export default TimeSheet;
